import React, { useState, useEffect, useContext } from "react";
import Main from "../../components/Main";
import TabelaDinamica from "../../components/TabelaDinamica";
import MensagemSucesso from "../../components/MensagemSucesso";
import MensagemErro from "../../components/MensagemErro";
import styles from "./styles.module.css";
import PersonIcon from "@mui/icons-material/Person";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  DialogContentText,
} from "@mui/material";
import CRUDPageHeader from "../../components/CRUDPageHeader";
import PaperComponent from "../../components/PaperComponent";
import { handleErrorResponse } from "../../utils/error.util";
import { Context } from "src/config/AuthContext";
import ValoresFaturaForm from "src/forms/ValoresFaturaForm";
import {
  atualizarValoresFatura,
  buscarValoresFatura,
  cadastrarValoresFatura,
  excluirValoresFatura,
  obterValoresFaturasPorCampo,
} from "../../services/api-financeiro.service";
import { formatToBRL } from "../../utils/currency.util";

const ValoresFatura = () => {
  const { handleLogout } = useContext(Context);

  const [loading, setLoading] = useState(false);
  const [editRowData, setEditRowData] = useState({});
  const [msg, setMsg] = useState("");

  const [idGrupoEconomico, setIdGrupoEconomico] = useState(null);
  const [idEmpresa, setIdEmpresa] = useState(null);

  const [openModal, setOpenModal] = useState(false);
  const [openDialogExclusao, setOpenDialogExclusao] = useState(false);
  const [openSnackbarErro, setOpenSnackbarErro] = useState(false);
  const [openSnackbarSucesso, setOpenSnackbarSucesso] = useState(false);

  const [data, setData] = useState([]);

  const columns = [
    {
      id: "idFinanceiroValorFatura",
      label: "ID",
      field: "idFinanceiroValorFatura",
    },
    { id: "servico", label: "Servico", field: "servico" },
    { id: "dtFatura", label: "Data", field: "dtFatura_cellVal" },
    { id: "valorFatura", label: "Valor", field: "valorFatura_cellVal", type: 'monetario' },
    { id: "historico", label: "Historico", field: "historico" },
  ];

  const getData = async () => {
    try {
      setLoading(true)
      const response = await buscarValoresFatura(idEmpresa);
      const valores = response.valoresFatura.map((valor) => {
        return {
          ...valor,
          razaoSocial: valor.EmpresasORM ? valor.EmpresasORM.razao_social : '-',
          cnpj: valor.EmpresasORM ? valor.EmpresasORM.cnpj : '-',
          dtFatura_cellVal: new Date(valor.dtFatura),
          valorFatura_cellVal: valor.valorFatura,
          servico: valor.ServicosORM.descricao
        };
      });
      setData(valores);
    } catch (err) {
      setMsg(err.mensagem);
      handleOpenSnackbarErro();
    } finally {
      setLoading(false)
    }
  };

  const handleOpenModal = async (rowData) => {
    rowData.idFinanceiroValorFatura !== undefined
      ? setEditRowData(rowData)
      : setEditRowData({});

    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  useEffect(() => {
    if (idGrupoEconomico && idEmpresa) getData();
    else setData([]);
  }, [idGrupoEconomico, idEmpresa]);

  const handleSaveChanges = async (data) => {
    setLoading(true);

    if (!editRowData.idFinanceiroValorFatura) {
      await cadastrarValoresFatura(idEmpresa, { valoresFatura: data.valoresFatura })
        .then(async (response) => {
          setMsg(response.mensagem);
          getData();
          handleCloseModal();
          handleOpenSnackbarSucesso();
        })
        .catch((err) =>
          handleErrorResponse(err, setMsg, handleOpenSnackbarErro, handleLogout)
        );
    } else {
      await atualizarValoresFatura(idEmpresa, editRowData.idFinanceiroValorFatura, {
        valoresFatura: data.valoresFatura,
      })
        .then(async (response) => {
          setMsg(response.mensagem);
          getData();
          handleCloseModal();
          handleOpenSnackbarSucesso();
        })
        .catch((err) =>
          handleErrorResponse(err, setMsg, handleOpenSnackbarErro, handleLogout)
        );
    }

    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    await excluirValoresFatura(idEmpresa, editRowData.idFinanceiroValorFatura)
      .then(async (response) => {
        setMsg(response.mensagem);
        getData();
        handleCloseDialogExclusao();
        handleOpenSnackbarSucesso();
      })
      .catch((err) =>
        handleErrorResponse(err, setMsg, handleOpenSnackbarErro, handleLogout)
      );
    setLoading(false);
  };

  const handleOpenSnackbarSucesso = () => {
    setOpenSnackbarSucesso(true);
  };

  const handleCloseSnackbarSucesso = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbarSucesso(false);
  };

  const handleOpenSnackbarErro = () => {
    setOpenSnackbarErro(true);
  };

  const handleCloseSnackbarErro = (event, reason) => {
    if (!reason === "clickaway") {
      return;
    }
    setOpenSnackbarErro(false);
  };

  const handleClickOpenDialogExclusao = (rowData) => {
    setEditRowData(rowData);
    setOpenDialogExclusao(true);
  };

  const handleCloseDialogExclusao = () => {
    setOpenDialogExclusao(false);
  };

  const handleLoginChange = (event) => {
    const loginValue = event.target.value
      .replace(/\s+/g, ".")
      .replace(/[^a-zA-Z.]/g, "")
      .replace(/\.+/g, ".")
      .toLowerCase();
    setEditRowData({ ...editRowData, login: loginValue });
  };

  const handleDebouncerChange = async (event) => {
    if (!idEmpresa) return;
    setLoading(true);
    try {
      if (event.target.value) {
        const response = await obterValoresFaturasPorCampo(
          idEmpresa,
          event.target.value,
        );
        let valores = response.valoresFatura;
        valores = await valores.map((valor) => {
          valor.servico = valor.ServicosORM.descricao;
          valor.razaoSocial = (valor.EmpresasORM) ? valor.EmpresasORM.razao_social : '-';
          valor.cnpj = (valor.EmpresasORM) ? valor.EmpresasORM.cnpj : '-';
          if (valor.dtFatura) valor.dtFatura_cellVal = new Date(valor.dtFatura);
          valor.valorFatura_cellVal = valor.valorFatura
          return valor;
        });
        setData(valores);
      } else {
        getData()
      }

    } catch (err) {
      if (err.response && err.response.status === 404) {
        setData([]);
      } else {
        handleErrorResponse(err, setMsg, handleOpenSnackbarErro, handleLogout);
      }
    }
    setLoading(false);
  };

  const handleGrupoECnpjChange = async (ids) => {
    setIdGrupoEconomico(ids.GE);
    setIdEmpresa(ids.CNPJ);
  };

  return (
    <div>
      <Main />

      <CRUDPageHeader
        headerIcon={
          <PersonIcon fontSize="30px"/>
        }
        headerTitle={"Valores Para Faturas"}
        handleDebounceChange={handleDebouncerChange}
        handleOpenModal={handleOpenModal}
        modalButtonLabel={"Adicionar"}
        AutoCompleteGrupoECnpj
        handleGrupoECnpjChange={handleGrupoECnpjChange}
      />

      <TabelaDinamica
        data={data}
        columns={columns}
        loading={loading}
        orderColumn={"idUsuario"}
        onEditButtonClick={handleOpenModal}
        onDeleteButtonClick={handleClickOpenDialogExclusao}
      />

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        PaperProps={{
          style: {
            width: "140%", // Defina a largura desejada
          },
        }}
        maxWidth="lg" // Defina o tamanho máximo do modal
      >
        <DialogTitle className={styles.popupHeader}>
          <div className={styles.linhaTituloPopup} />
          <div className={styles.tituloPopupContainer}>
            <PersonIcon className={styles.iconeTituloPopup} />
            <h1 className={styles.tituloPopup}>
              {editRowData.idFinanceiroValorFatura
                ? "Edição de ValoresFatura"
                : "Valores Para Faturas"}
            </h1>
          </div>
        </DialogTitle>
        <ValoresFaturaForm
          valoresFaturaValues={Object.assign({}, editRowData)}
          onSubmit={handleSaveChanges}
          onClose={handleCloseModal}
        />
      </Dialog>

      {/* Dialog de exclusão */}
      <Dialog
        open={openDialogExclusao}
        onClose={handleCloseDialogExclusao}
        PaperComponent={PaperComponent}
        aria-labelledby="draggable-dialog-title"
      >
        <DialogTitle
          className={styles.tituloExclusao}
          style={{ cursor: "move" }}
          id="draggable-dialog-title"
        >
          Atenção!
        </DialogTitle>
        <DialogContent>
          <DialogContentText className={styles.textoExclusao}>
            Você realmente deseja excluir o valor de fatura ID {editRowData.idFinanceiroValorFatura}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            className={styles.botaoPopupCancelarExclusao}
            autoFocus
            onClick={handleCloseDialogExclusao}
          >
            Cancelar
          </Button>
          <Button className={styles.botaoPopupExcluir} onClick={handleDelete}>
            Sim
          </Button>
        </DialogActions>
      </Dialog>

      <MensagemSucesso
        open={openSnackbarSucesso}
        handleClose={handleCloseSnackbarSucesso}
        msg={msg}
      />

      <MensagemErro
        open={openSnackbarErro}
        handleClose={handleCloseSnackbarErro}
        msg={msg}
      />
    </div>
  );
};

export default ValoresFatura;
